import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Grade } from '@core/sim/cosmetic/cosmeticCatalog';
import { parseCardKey } from '@core/sim/cosmetic/cosmeticCatalog';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/useAuth';

export type EquippedCompanion = {
  slug: string;
  grade: Grade;
  level: number;
  cardId: string | null;
  cardKey: string | null;
  isDefault: boolean;
  /** 첫 조회가 끝나기 전. true인 동안 slug는 기본값(황둥이)이라 **화면이 그대로 그리면 깜빡인다** —
   *  "아직 모름"과 "장착 없음"을 구분하려고 둔다(2026-09-10). 캐시가 있으면 재진입 시 false. */
  isLoading: boolean;
};

/** 장착 조회 캐시 키 prefix — 장착을 바꾸는 쪽(카드 컬렉션·가챠 바로 장착)이 이걸로 무효화한다. */
export const COSMETIC_QUERY_KEY = ['cosmetic'] as const;

const DEFAULT_COMPANION: EquippedCompanion = {
  slug: 'hwangdoongi', grade: 'N', level: 0, cardId: null, cardKey: null, isDefault: true, isLoading: false,
};

export function resolveEquippedCompanion(data: any, slots: string[]): EquippedCompanion {
  const cards = new Map((data?.cards || []).map((card: any) => [card.id, card]));
  for (const slot of slots) {
    const equip = (data?.equips || []).find((row: any) => row.slot_key === slot);
    if (!equip) continue;
    if (equip.base_cast) return { ...DEFAULT_COMPANION, slug: equip.base_cast, isDefault: false };
    const card: any = cards.get(equip.card_id);
    const parsed = parseCardKey(card?.card_key || '');
    if (card && parsed) return {
      slug: parsed.slug,
      grade: parsed.grade,
      level: Number(card.enhance_level) || 0,
      cardId: card.id,
      cardKey: card.card_key,
      isDefault: false,
      isLoading: false,
    };
  }
  return DEFAULT_COMPANION;
}

/**
 * 🔴 2026-09-10 React Query 전환. 종전엔 `useState(null)` + `useEffect` 직접 fetch였다:
 *  ① 첫 렌더가 항상 null → `DEFAULT_COMPANION`(황둥이·isDefault)을 반환해, 로드 전까지
 *     **황둥이 + 회색 + "내 캐릭터 고르기"가 그려졌다가 한꺼번에 바뀌었다**(효신님 SE3 MY탭).
 *  ② 캐시가 없어 이 훅을 쓰는 화면(6곳)에 들어갈 때마다 매번 null부터 다시 시작했다.
 * 이제 같은 키를 공유하는 캐시로 재진입은 즉시 뜨고, 첫 로드는 `isLoading`으로 구분할 수 있다.
 * 키에 user.id를 넣는다 — 계정 전환 시 이전 계정 장착이 보이면 안 된다.
 */
export function useEquippedCompanion(slots: string[]): EquippedCompanion {
  const { user } = useAuth();
  const slotKey = slots.join('|');
  const { data, isLoading } = useQuery({
    queryKey: [...COSMETIC_QUERY_KEY, user?.id ?? 'guest'],
    queryFn: () => apiFetch('/api/mobile/cosmetic', { cache: 'no-store' }),
    enabled: !!user,   // 게스트는 조회하지 않는다 → isLoading=false · 기본값 황둥이(정상)
  });
  return useMemo(
    () => ({ ...resolveEquippedCompanion(data ?? null, slotKey.split('|')), isLoading }),
    [data, slotKey, isLoading],
  );
}

